import { FilterDefinition, Operators } from "@monolith-forensics/monolith-ui";
import { CustomField } from "./custom-fields.types";

export type CustomFieldFilterDefinition = Omit<
  FilterDefinition,
  "dataField"
> & {
  dataField: `custom_field_${number}`;
};

/**
 * Maps a storage or asset custom field to a `FilterDefinition`; both go
 * through the API's shared filterResolver. Mirrors the Evidence
 * `resolveCustomFieldFilter`, but constrains operator sets to those that
 * resolver supports — no Equals/NotEquals on dates and no inclusive (>=/<=)
 * date operators (see ENG-1315/1317 backend limitation).
 */
export const resolveCustomFieldFilter = (
  field: CustomField
): CustomFieldFilterDefinition => {
  const filter: CustomFieldFilterDefinition = {
    label: field.field_name,
    dataField: `custom_field_${field.field_id}`,
    inputType: "text",
    operators: [
      Operators.Equals,
      Operators.NotEquals,
      Operators.Contains,
      Operators.DoesNotContain,
    ],
  };

  switch (field.editor_type) {
    case "textBox":
      filter.inputType = "text";
      filter.operators = [
        Operators.Equals,
        Operators.NotEquals,
        Operators.Contains,
        Operators.DoesNotContain,
      ];
      break;
    case "dropDown":
    case "tagBox":
      filter.inputType = "multiselect";
      filter.operators = [Operators.In, Operators.NIn];
      filter.selectOptions =
        field.options?.map((option) => ({
          label: option,
          value: option,
          data: option,
        })) || [];
      break;
    case "dateBox":
      filter.inputType = "datetime";
      filter.resolution = "day";
      // No Equals/NotEquals: the resolver runs raw `= ?` against full ISO
      // timestamps, so day-resolution equality never matches.
      filter.operators = [
        Operators.GreaterThan,
        Operators.LessThan,
        Operators.Between,
      ];
      break;
    default:
      break;
  }

  return filter;
};
